import type { Components } from "@mui/material/styles";

import type { Theme } from "../types";

/**
 * The one surface a hover tooltip is allowed to have.
 *
 * The house direction is that a tooltip is the exception — helper text under the field is the
 * rule — so the few that survive have to look deliberate rather than like a browser default.
 * That means the same three things every other floating surface in this theme has: a solid
 * ground, a `divider` hairline, and a real shadow. What was here before was a `backdropFilter`
 * blur over MUI's default 92%-opaque grey, which on the dark ground read as a smear rather than
 * a panel and let bright content behind it bleed through the copy.
 *
 * `maxWidth: 320` is the ceiling the copy is written against: anything that needs more than about
 * two lines at that width is not a tooltip, it is helper text, and belongs under the field.
 *
 * Placement is `top` with 8px of collision padding on both `flip` and `preventOverflow`, so a
 * tooltip on a chip in the last table row turns over instead of being clipped by the viewport.
 */
export const MuiTooltip = {
	defaultProps: {
		arrow: true,
		placement: "top",
		// Long enough that dragging the pointer across a table does not trail tooltips behind it.
		enterDelay: 300,
		enterNextDelay: 300,
		leaveDelay: 0,
		slotProps: {
			popper: {
				modifiers: [
					{ name: "offset", options: { offset: [0, 8] } },
					{ name: "flip", options: { padding: 8 } },
					{ name: "preventOverflow", options: { padding: 8 } },
				],
			},
		},
	},
	styleOverrides: {
		tooltip: ({ theme }) => ({
			backgroundColor: "var(--mui-palette-background-level1)",
			backgroundImage: "none",
			color: "var(--mui-palette-text-primary)",
			border: "1px solid var(--mui-palette-divider)",
			borderRadius: "10px",
			boxShadow: theme.vars?.shadows[16] ?? theme.shadows[16],
			maxWidth: 320,
			padding: "8px 12px",
			fontSize: "var(--fontSize-sm)",
			fontWeight: 400,
			lineHeight: 1.5,
		}),
		arrow: {
			color: "var(--mui-palette-background-level1)",
			// The arrow is a rotated square, so the hairline has to be drawn on it separately or
			// the panel's border stops dead where the arrow starts.
			"&::before": {
				backgroundColor: "var(--mui-palette-background-level1)",
				border: "1px solid var(--mui-palette-divider)",
			},
		},
	},
} satisfies Components<Theme>["MuiTooltip"];
